import {
  AppSessionCellId,
  CellId,
  CellRunMode,
  RUN_APP_SESSION,
  assertNever,
  serializeRichText,
} from "@hex/common";
import React, { useCallback } from "react";

import { useToaster } from "../../components/common/Toasts";
import { LinkIcon } from "../../components/icons/CustomIcons";
import { Flex, Txt } from "../../hex-components";
import { useCellContentsGetter } from "../../hex-version-multiplayer/state-hooks/cellContentsStateHooks";
import { useCellGetter } from "../../hex-version-multiplayer/state-hooks/cellStateHooks.js";
import {
  canRunPendingMagicEvent,
  useMagicEventsGetter,
} from "../../hex-version-multiplayer/state-hooks/magicEventStateHooks.js";
import { useSelector, useStore } from "../../redux/hooks";
import { appSessionMPSelectors } from "../../redux/slices/appSessionMPSlice";
import { CellContentsMP } from "../../redux/slices/hexVersionMPSlice";
import { useRunEventBasedAppSessionMutation } from "../../route/AppRoute.generated";
import { getEditorForCellId } from "../../state/models/useEditor";
import { getModel } from "../../state/models/useModel";
import { useAppSessionAOContext } from "../../util/appSessionAOContext";
import { useProjectContext } from "../../util/projectContext";
import { RunCellArgs as OrigRunCellArgs, runCell } from "../../util/runCell";
import { useSessionContext } from "../../util/sessionContext";

/**
 * Determines if a text selection should be treated as a valid selection for SQL execution.
 * Only selections containing multiple meaningful words/tokens are considered valid,
 * as single-word selections (even with whitespace) are typically for editing rather than execution.
 */
export function isValidSqlSelection(selectedText: string): boolean {
  if (selectedText.length < 3) {
    return false;
  }

  // Split by whitespace and filter out empty strings to count meaningful tokens
  const meaningfulTokens = selectedText
    .split(/\s+/)
    .filter((token) => token.length > 0);
  return meaningfulTokens.length >= 2;
}

interface RunCellArgs {
  cellId: CellId;
  cellRunMode?: CellRunMode;
  appSessionCellId: AppSessionCellId | undefined;
  forceOverwriteCache?: boolean;
  executeTrace?: boolean;
  forLogicView?: boolean;
  maybeSkipSql?: boolean;
}

export interface UseRunCellResult {
  runCell: (args: RunCellArgs) => Promise<void>;
}

export function useRunCell(): UseRunCellResult {
  const { dispatchAO: dispatchAppSessionAO } = useAppSessionAOContext();
  const getCell = useCellGetter({});
  const getMagicEvents = useMagicEventsGetter();

  // Get userModifiedDiffs from Redux state
  const cellUserModifiedDiffs = useSelector(
    (state) => state.logicView.cellUserModifiedDiffs,
  );

  const runCellArgsGetter = useCallback(
    (
      cellContents: CellContentsMP,
      {
        appSessionCellId,
        cellId,
        cellRunMode,
        executeTrace,
        forLogicView,
        forceOverwriteCache,
        maybeSkipSql,
      }: RunCellArgs,
    ): OrigRunCellArgs | undefined => {
      const cell = getCell(cellId);
      const magicEvent = cell.latestMagicEventId
        ? getMagicEvents()[cell.latestMagicEventId]
        : null;

      // if there is a pending magic edit or fix (ie, not an insert), we'd like to run that event instead of the cell's source
      const magicEventToRun =
        magicEvent && canRunPendingMagicEvent(magicEvent) ? magicEvent : null;
      let source: string | undefined = "";

      // Check if there's a user-modified diff for this cell
      const userModifiedDiff = cellUserModifiedDiffs[cellId];

      switch (cellContents.__typename) {
        case "TextCell": {
          return {
            cellId,
            cellRunMode,
            appSessionCellId,
            forceOverwriteCache,
            executeTrace,
            dispatchAppSessionAO,
            forLogicView,
            source: serializeRichText(cellContents.richText),
            maybeSkipSql,
          };
        }
        case "CodeCell":
        case "SqlCell":
        case "MarkdownCell": {
          let runMode = cellRunMode;

          // we want to eagerly use the model source to prevent running
          // with stale source if its available since we locally debounce model source updates
          const model = getModel(cellContents.cellId);
          source = model?.getValue() ?? cellContents.source;
          let linesRun: [number, number] | undefined = undefined;

          // If it's a SQL cell, we want to check whether there's a selected block of text.
          // If there is a selection, we want to run that instead of the full cell source.
          if ("SqlCell" === cellContents.__typename && model != null) {
            const editor = getEditorForCellId(cellContents.cellId);
            if (editor != null) {
              const selection = editor.getSelection();
              if (selection && !selection.isEmpty()) {
                const selectedText = model.getValueInRange(selection);
                // Only treat as a valid selection if it contains whitespace (more than a single word)
                if (isValidSqlSelection(selectedText)) {
                  source = selectedText;
                  runMode = CellRunMode.CELL_ONLY;
                  linesRun = [
                    selection.startLineNumber,
                    selection.endLineNumber,
                  ];
                }
              }
            }
          }

          if (magicEventToRun) {
            source = userModifiedDiff ?? magicEventToRun.result ?? undefined;
            runMode = CellRunMode.CELL_ONLY;
          }

          return {
            cellId,
            cellRunMode: runMode,
            appSessionCellId,
            forceOverwriteCache,
            executeTrace,

            source,
            linesRun,
            dispatchAppSessionAO,
            forLogicView,
            maybeSkipSql,
            isPendingMagicDiffRun: !!magicEventToRun,
          };
        }
        // TODO(writeback) - figure this out
        case "WritebackCell":
        case "DisplayTableCell":
        case "MetricCell":
        case "VegaChartCell":
        case "Parameter":
        case "DbtMetricCell":
        case "MapCell":
        case "ChartCell":
        case "PivotCell":
        case "FilterCell":
        case "BlockCell":
        case "ExploreCell":
          return {
            cellId,
            cellRunMode,
            appSessionCellId,
            forceOverwriteCache,
            executeTrace,
            dispatchAppSessionAO,
            forLogicView,
            maybeSkipSql,
          };
        case "CollapsibleCell":
        case "ComponentImportCell":
          return;
        default:
          assertNever(
            cellContents,
            (cellContents as { __typename: string }).__typename,
          );
      }
    },
    [dispatchAppSessionAO, getCell, getMagicEvents, cellUserModifiedDiffs],
  );

  const getRunCellArgs = useCellContentsGetter({
    selector: runCellArgsGetter,
  });

  // we try our best to only read data on demand
  // to prevent us from invalidating the returned callback
  const runCellCallback = useCallback(
    async (args: RunCellArgs) => {
      const runCellArgs = getRunCellArgs(args.cellId, args);

      if (runCellArgs == null) {
        return;
      }
      await runCell(runCellArgs);
    },
    [getRunCellArgs],
  );

  return { runCell: runCellCallback };
}

export function useRunWithTrace(
  forceOverwriteCache: boolean = true,
): () => Promise<void> {
  const { hexId } = useProjectContext();
  const { appSessionId } = useSessionContext();
  const toaster = useToaster();
  const { dispatchAO } = useAppSessionAOContext();
  const store = useStore();
  const [runEventBasedAppSession] = useRunEventBasedAppSessionMutation();

  return useCallback(async () => {
    const useEvents = appSessionMPSelectors
      .getAppSessionSelectors(appSessionId)
      .selectUseEvents(store.getState());

    if (useEvents) {
      await runEventBasedAppSession({
        variables: {
          appSessionId,
          executeTrace: true,
          forceOverwriteCache,
        },
        context: {
          enableTracing: true,
        },
      });
    } else {
      await dispatchAO(
        RUN_APP_SESSION.create({
          forceOverwriteCache,
          executeTrace: true,
        }),
        {
          enableTracing: true,
        },
      ).promise;
    }

    toaster.show({
      message: (
        <div css="width: 350px">
          <p>
            <Txt fontWeight="semi_bold">Go to Datadog to see the trace.</Txt>
          </p>
          <p>
            It may take a few minutes after cells have finished running for the
            trace to show up in Datadog.
          </p>
        </div>
      ),
      intent: "primary",
      action: {
        text: (
          <Flex align="center" gap={4}>
            <LinkIcon />
            <span>Open Datadog</span>
          </Flex>
        ),
        onClick: () => {
          window.open(
            `https://app.datadoghq.com/apm/traces?query=service%3Ahex.sidecar%20operation_name%3AenqueuedBatch%20%40hexId%3A${hexId}&agg_m=count&agg_m_source=base&agg_t=count&cols=service%2Cresource_name%2C%40duration%2C%40_span.count%2C%40_duration.by_service%2C%40orgId%2C%40hexId%2C%40&saved-view-id=3299621&sort=desc&spanType=all`,
            "_blank",
          );
        },
      },
      timeout: 30_000,
    });
  }, [
    appSessionId,
    dispatchAO,
    forceOverwriteCache,
    hexId,
    runEventBasedAppSession,
    store,
    toaster,
  ]);
}
